import React, { ReactNode, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Animated, Dimensions, Modal, SafeAreaView } from 'react-native';
import styled from 'styled-components/native';

import { EDataTestId, EDataTestIdSuffix } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { isRTL, useTranslation } from '@webtrader/core/i18n';
import { rgba } from '@webtrader/core/themes/utils';
import { checkIsNative } from '@webtrader/core/validators/device';

import Button from '@/components/Button';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';
import { useDeviceDimensions } from '@/utils/device';

import { ACTION_SHEET_STYLES } from '../Settings';

const SPRING_SPEED = 16;
const CLOSE_DURATION = 300;

const IS_NATIVE = checkIsNative();

export enum EBottomSheetType {
  DEFAULT = 'DEFAULT',
  CONFIRMATION = 'CONFIRMATION',
  ORDER = 'ORDER',
  BALANCE = 'BALANCE'
}

interface IActionSheetBottomSheetProps {
  children?: ReactNode;
  type?: EBottomSheetType;
  title?: string;
  visible?: boolean;
  testID?: EDataTestId;
  onClose?: (isCancel?: boolean) => void;
  onConfirm?: () => void;
  onDismiss?: () => void;
  closeOnOverlayPress?: boolean;
  minimal?: boolean;
}

export interface IActionSheetRef {
  closeActionSheet: (callback?: () => void) => void;
}

const ActionSheetBottomSheet = forwardRef<IActionSheetRef, IActionSheetBottomSheetProps>(
  (props: IActionSheetBottomSheetProps, ref) => {
    const {
      visible,
      type = EBottomSheetType.DEFAULT,
      title,
      testID,
      onClose,
      onConfirm,
      onDismiss,
      children,
      closeOnOverlayPress = true,
      minimal = false
    } = props;

    const { t } = useTranslation();

    const [show, setShow] = useState<boolean>(false);
    const [animation, setAnimation] = useState<Animated.Value>(new Animated.Value(Dimensions.get('screen').height));

    const { height } = useDeviceDimensions((nextHeight: number) => {
      setAnimation(new Animated.Value(nextHeight));
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          closeActionSheet(callback?: () => void) {
            Animated.timing(animation, {
              toValue: height,
              duration: CLOSE_DURATION,
              useNativeDriver: IS_NATIVE
            }).start();

            setTimeout(() => {
              setShow(false);
              onClose?.(!callback);
              callback?.();
            }, CLOSE_DURATION);
          }
        };
      },
      [animation, height, onClose]
    );

    const closeActionSheet = useCallback(
      (callback?: () => void) => {
        Animated.timing(animation, {
          toValue: height,
          duration: CLOSE_DURATION,
          useNativeDriver: IS_NATIVE
        }).start();

        setTimeout(() => {
          setShow(false);
          onClose?.(!callback);
          callback?.();
        }, CLOSE_DURATION);
      },
      [height, animation, onClose]
    );

    const handleClose = useCallback(() => {
      closeActionSheet();
    }, [closeActionSheet]);

    const handleConfirm = useCallback(() => {
      closeActionSheet(onConfirm);
    }, [onConfirm, closeActionSheet]);

    useEffect(() => {
      if (visible) {
        setShow(true);
        Animated.spring(animation, {
          toValue: 0,
          speed: SPRING_SPEED,
          bounciness: 1,
          useNativeDriver: IS_NATIVE
        }).start();
      }
    }, [animation, visible]);

    useEffect(() => {
      if (show && !visible) {
        handleClose();
      }
    }, [handleClose, show, visible]);

    return (
      <Modal
        visible={show}
        transparent={true}
        animationType="none"
        supportedOrientations={['landscape', 'portrait']}
        onDismiss={onDismiss}
        dir={isRTL() ? 'rtl' : 'ltr'}
      >
        <StyledModalContainer>
          <StyledOverlay activeOpacity={1} onPress={closeOnOverlayPress ? handleClose : () => {}} />
          <StyledSheetSlider style={[{ transform: [{ translateY: animation }] }]} testID={testID}>
            {minimal ? (
              <StyledSafeAreaView>{children}</StyledSafeAreaView>
            ) : (
              <StyledSafeAreaView>
                {type === EBottomSheetType.CONFIRMATION && (
                  <StyledSheetHeaderSection>
                    <Button variant="subtle" textColor="light" onPress={handleClose}>
                      {t('CANCEL')}
                    </Button>
                    <StyledSheetTitleText>{title}</StyledSheetTitleText>
                    <Button variant="subtle" onPress={handleConfirm}>
                      {t('CONFIRM')}
                    </Button>
                  </StyledSheetHeaderSection>
                )}
                {type !== EBottomSheetType.CONFIRMATION && !!title && (
                  <StyledSheetHeaderSection>
                    <StyledSheetTitleText testID={`${testID}-${EDataTestIdSuffix.TITLE.toLowerCase()}`}>
                      {title}
                    </StyledSheetTitleText>
                  </StyledSheetHeaderSection>
                )}
                <StyledSheetChildren maxHeight={height}>{children}</StyledSheetChildren>
                {type === EBottomSheetType.DEFAULT && (
                  <StyledSheetCancelButton
                    activeOpacity={1}
                    onPress={handleClose}
                    testID={EDataTestId.ACTION_SHEET_CANCEL_BUTTON}
                  >
                    <StyledSheetCancelButtonText>{t('CANCEL')}</StyledSheetCancelButtonText>
                  </StyledSheetCancelButton>
                )}
              </StyledSafeAreaView>
            )}
          </StyledSheetSlider>
        </StyledModalContainer>
      </Modal>
    );
  }
);

export default memo(ActionSheetBottomSheet);

const StyledModalContainer = styled.View`
  flex: 1;
  justify-content: flex-end;
`;

const StyledOverlay = styled(TouchableOpacity)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${rgba('rgb(0, 0, 0)', 0.6)};
`;

const StyledSheetSlider = styled(Animated.View)`
  justify-content: flex-end;
  overflow: hidden;

  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  background-color: ${({ theme }) => theme.colors.background[0]};
`;

const StyledSheetChildren = styled.ScrollView<{ maxHeight: number }>`
  min-height: 50px;
  max-height: ${({ maxHeight }) => (checkIsNative() ? maxHeight * 0.8 : maxHeight * 0.6)}px;
`;

const StyledSheetCancelButton = styled(TouchableOpacity)`
  height: ${ACTION_SHEET_STYLES.ACTION_BUTTON.height}px;

  align-items: center;
  justify-content: center;

  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.border[0]};
  background-color: ${({ theme }) => theme.colors.background[0]};
`;

const StyledSheetCancelButtonText = styled(Text)`
  font-size: ${ACTION_SHEET_STYLES.ACTION_BUTTON.fontSize}px;
  font-weight: 500;
`;

const StyledSheetHeaderSection = styled.View`
  height: ${dynamicSize.convertSize(44)}px;

  padding-start: ${({ theme }) => theme.sizes.mobile.basePaddingStart}px;
  padding-end: ${({ theme }) => theme.sizes.mobile.basePaddingEnd}px;

  align-items: center;
  flex-direction: row;

  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.colors.border[0]};
`;

const StyledSheetTitleText = styled(Text)`
  font-size: ${dynamicSize.convertSize(16)}px;
  font-weight: 500;
  flex: 1;
  text-align: center;
`;

const StyledSafeAreaView = styled(SafeAreaView)`
  background-color: ${({ theme }) => theme.colors.background[0]};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;
